import { useCallback, useRef } from "react"

import type { FetchNextPageOptions } from "@tanstack/react-query"

import type { GridEventListener } from "@mui/x-data-grid-pro"

interface BaseRecord {
  id: number | string
}

export interface PaginatedRecord extends BaseRecord {
  page: number
  pageIndex: number
  pageSize: number
}

export interface PageStats {
  number: number
  pages: number
  size: number
  total: number
}

interface MetaWithPageStats {
  stats: {
    page: {
      count: PageStats
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface GraphitiRecord<TRecord = Record<string, any>> {
  attributes: TRecord
  id: string
  type: string
}

interface PaginatedGraphitiCollectionResponse<TRecord extends PaginatedRecord> {
  data: GraphitiRecord<TRecord>[]
  meta: MetaWithPageStats
}

export interface PaginatedListResponse<
  TRecord extends BaseRecord = BaseRecord,
> {
  items: TRecord[]
  limit: number
  offset: number
  total: number
}

export const graphitiCollectionResponseToPaginatedData = <
  TRecord extends PaginatedRecord,
>(
  response: PaginatedGraphitiCollectionResponse<TRecord>
): PaginatedData<GraphitiRecord<TRecord>> => {
  return {
    items: response.data,
    page: response.meta.stats.page.count.number,
    pages: response.meta.stats.page.count.pages,
    size: response.meta.stats.page.count.size,
    total: response.meta.stats.page.count.total,
  }
}

export const paginatedListResponseToPaginatedData = <
  TRecord extends BaseRecord,
  TResponse extends PaginatedListResponse<TRecord>,
>(
  response: TResponse
): TResponse & PaginatedData<TRecord> => {
  const pages = Math.max(1, Math.ceil(response.total / response.limit))

  return {
    ...response,
    page: Math.floor(response.offset / response.limit) + 1,
    pages,
    size: response.limit,
  }
}

export interface PaginatedData<TRecord extends BaseRecord = BaseRecord> {
  items: TRecord[]
  /** page number of this page */
  page: number
  /** total number of pages */
  pages: number
  /** page size */
  size: number
  /** total number of items */
  total: number
}

interface UseInfiniteScrollOptions {
  fetchNextPage: (options?: FetchNextPageOptions) => unknown
  fetchPreviousPage?: (options?: FetchNextPageOptions) => unknown
  hasNextPage?: boolean
  hasPreviousPage?: boolean
  /** is the query enabled? similar to react query enabled option */
  isEnabled: boolean
  isFetchingNextPage: boolean
  isFetchingPreviousPage?: boolean
}

interface UseInfiniteScrollValue {
  handleOnRowsScrollEnd: GridEventListener<"rowsScrollEnd">
  handleOnScrollTopChange: (top: number) => void
  handleOnWheelUpAtTop: () => void
}

export function useInfiniteScroll({
  fetchNextPage,
  fetchPreviousPage,
  hasNextPage,
  hasPreviousPage,
  isEnabled,
  isFetchingNextPage,
  isFetchingPreviousPage = false,
}: UseInfiniteScrollOptions): UseInfiniteScrollValue {
  const previousScrollTopRef = useRef<number | null>(null)
  const lastPreviousFetchAtMsRef = useRef(0)
  const fetchPreviousIfAvailable = useCallback(() => {
    const now = Date.now()
    const minimumFetchIntervalMs = 250
    if (
      fetchPreviousPage &&
      isEnabled &&
      hasPreviousPage &&
      !isFetchingPreviousPage &&
      now - lastPreviousFetchAtMsRef.current >= minimumFetchIntervalMs
    ) {
      lastPreviousFetchAtMsRef.current = now
      void fetchPreviousPage()
    }
  }, [fetchPreviousPage, hasPreviousPage, isEnabled, isFetchingPreviousPage])

  /**
   * Fetch the next page when the user scrolls to the end of the current page
   */
  // Handle by passing prop to DataGrid instead of api
  // because apiRef.current.subscribeEvent("rowsScrollEnd", handleOnRowsScrollEnd) is broken in current version
  const handleOnRowsScrollEnd = useCallback(() => {
    if (isEnabled && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isEnabled, isFetchingNextPage])

  const handleOnScrollTopChange = useCallback(
    (top: number) => {
      if (!fetchPreviousPage || !isEnabled) {
        return
      }

      const previousTop = previousScrollTopRef.current
      previousScrollTopRef.current = top

      const isScrollingUp = previousTop !== null && top < previousTop
      const isNearTop = top <= 8

      if (isScrollingUp && isNearTop && hasPreviousPage) {
        fetchPreviousIfAvailable()
      }
    },
    [
      fetchPreviousIfAvailable,
      fetchPreviousPage,
      hasPreviousPage,
      isEnabled,
      previousScrollTopRef,
    ]
  )

  const handleOnWheelUpAtTop = useCallback(() => {
    fetchPreviousIfAvailable()
  }, [fetchPreviousIfAvailable])

  return {
    handleOnWheelUpAtTop,
    handleOnScrollTopChange,
    handleOnRowsScrollEnd,
  }
}
